import { SaveCustomWorklistToDashboardDTO } from '@/Models/API/WorklistsDashboard/SaveCustomWorklistToDashboardDTO';
import { WorklistsDashboardTickerDTO } from '@/Models/API/WorklistsDashboard/WorklistsDashboardDataForUserDTO';
import { isEntityLoaded } from '@/Models/Entity';
import { IWorklistsDashboardStore, MAX_WORKLISTS_IN_DASHBOARD } from '@/Stores/WorklistsDashboard/WorklistsDashboardStore';
import { ICustomWorklistHelper } from './CustomWorklistHelper';

export interface ICurrentWorklistDashboardHelper {
    currentWorklistDashboardTickerInfo?: WorklistsDashboardTickerDTO;
    isMaxWorklistsInDashboard: boolean;
    isWorklistAlreadyInDashboard: boolean;
    deleteWorklistDashboard: () => Promise<void>;
    saveWorklistDashboard: (abbreviation: string, highThreshold: number, mediumThreshold: number) => Promise<void>;
    get isNoSpaceLeftForCurrentNewDashboard(): boolean;
    get dialogActionTitleKey(): string;
}

export const MAX_ABBREVIATION_LENGTH = 5;

export class CurrentWorklistDashboardHelper implements ICurrentWorklistDashboardHelper {
    private readonly customWorklistStore: ICustomWorklistHelper;
    private readonly worklistsDashboardStore: IWorklistsDashboardStore;

    constructor(
        customWorklistStore: ICustomWorklistHelper,
        worklistsDashboardStore: IWorklistsDashboardStore,
    ) {
        this.customWorklistStore = customWorklistStore;
        this.worklistsDashboardStore = worklistsDashboardStore;
    }

    get currentWorklistDashboardTickerInfo(): WorklistsDashboardTickerDTO | undefined {
        const dashboardStoreState = this.worklistsDashboardStore.state;
        if (!isEntityLoaded(dashboardStoreState)) {
            return undefined;
        }

        const currentWorklistID = this.customWorklistStore.currentlySelectedCustomWorklist?.ID;
        if (!currentWorklistID) {
            return undefined;
        }

        const allTickers = dashboardStoreState.WorklistsDashboardTickers;
        return allTickers.find((tickerInfo) => tickerInfo.Info.CustomWorklistID === currentWorklistID);
    }

    get isMaxWorklistsInDashboard(): boolean {
        const dashboardStoreState = this.worklistsDashboardStore.state;
        if (!isEntityLoaded(dashboardStoreState)) {
            return false;
        }

        const amountOfWorklistsInDashboard = dashboardStoreState.WorklistsDashboardTickers.length;
        return amountOfWorklistsInDashboard >= MAX_WORKLISTS_IN_DASHBOARD;
    }

    get isWorklistAlreadyInDashboard(): boolean {
        return !!this.currentWorklistDashboardTickerInfo;
    }

    deleteWorklistDashboard = async (): Promise<void> => {
        const currentWorklistID = this.customWorklistStore.currentlySelectedCustomWorklist?.ID;
        if (!currentWorklistID) {
            throw new Error(`❌ Can't delete the dashboard ticker for the current worklist since no current worklist has been selected`);
        }

        await this.worklistsDashboardStore.deleteCustomWorklistFromDashboard(currentWorklistID);
    };

    saveWorklistDashboard = async (abbreviation: string, highThreshold: number, mediumThreshold: number): Promise<void> => {
        const currentWorklistID = this.customWorklistStore.currentlySelectedCustomWorklist?.ID;
        if (!currentWorklistID) {
            throw new Error(`❌ Can't add the dashboard ticker for the current worklist since no current worklist selected`);
        }

        const wouldBeAddingNewWorklistToDashboard = !this.isWorklistAlreadyInDashboard;
        const isExceedingMaxWorklistsInDashboard = wouldBeAddingNewWorklistToDashboard && this.isMaxWorklistsInDashboard;
        if (isExceedingMaxWorklistsInDashboard) {
            throw new Error(`❌ Can't add any more worklists to dashboard, max of ${MAX_WORKLISTS_IN_DASHBOARD} reached`);
        }

        const saveData = {
            Abbreviation: abbreviation,
            CustomWorklistID: currentWorklistID,
            HighThreshold: highThreshold,
            MediumThreshold: mediumThreshold,
        } satisfies SaveCustomWorklistToDashboardDTO;
        return await this.worklistsDashboardStore.saveCustomWorklistToDashboard(saveData);
    };

    get isNoSpaceLeftForCurrentNewDashboard(): boolean {
        const isNewDashboard = !this.isWorklistAlreadyInDashboard;
        const noSpaceLeft = this.isMaxWorklistsInDashboard;

        return isNewDashboard && noSpaceLeft;
    }

    get dialogActionTitleKey(): string {
        const isWorklistAlreadyInDashboard = this.isWorklistAlreadyInDashboard;
        return isWorklistAlreadyInDashboard
            ? 'EditWorklistDashboard'
            : 'AddWorklistToDashboard';
    }
}
